import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { adminBillingCycleService } from '@/services/admin-billing-cycle.service'
import { awaitRefresh } from '@/lib/awaitRefresh'
import type {
  BillingCycleFilters,
  UpdateBillingCycleRequest,
} from '@/types/billing-cycle'

/**
 * Query keys for billing-cycle queries
 */
export const billingCycleKeys = {
  all: ['billing-cycles'] as const,
  lists: () => [...billingCycleKeys.all, 'list'] as const,
  list: (filters: BillingCycleFilters) => [...billingCycleKeys.lists(), filters] as const,
  expiryMismatches: (page: number, pageSize: number) =>
    [...billingCycleKeys.all, 'expiry-mismatches', page, pageSize] as const,
  resourcesWithoutCycle: (page: number, pageSize: number) =>
    [...billingCycleKeys.all, 'resources-without-cycle', page, pageSize] as const,
}

/**
 * Hook to fetch the paginated billing-cycle list with filters
 * Returns: { data: BillingCycle[]; pagination: PaginationInfo }
 */
export function useBillingCycles(filters: BillingCycleFilters) {
  return useQuery({
    queryKey: billingCycleKeys.list(filters),
    queryFn: () => adminBillingCycleService.getBillingCycles(filters),
    staleTime: 30000, // 30 seconds
    placeholderData: (previousData) => previousData, // keep the table visible while a new page/filter loads
  })
}

/**
 * Hook to correct a billing cycle's window and status.
 *
 * Invalidates the whole billing-cycle key rather than patching the cached row: the list is
 * filtered and paginated server-side, and a status change can move the row out of the page the
 * operator is looking at — a local patch would leave it showing a row the server no longer returns.
 */
export function useUpdateBillingCycle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBillingCycleRequest }) =>
      adminBillingCycleService.updateBillingCycle(id, payload),
    onSuccess: () => {
      toast.success('Billing cycle updated')
    },
    // No error toast: the expiry screen shows the API's message in the panel that saved.
    // Returned (not `void`-fired) and moved to onSettled: the mutation must stay `isPending` until
    // the refetch it triggers has landed, so a caller gating a form's disabled state on
    // `isPending` doesn't re-enable it while the list still shows the pre-edit row.
    // `awaitRefresh` caps that wait at 8s so a stalled re-read cannot hang the caller's overlay —
    // the refetch itself keeps running past the cap and the screen catches up when it lands.
    onSettled: () => awaitRefresh(queryClient.invalidateQueries({ queryKey: billingCycleKeys.all })),
  })
}

/**
 * Hook for the expiry-mismatch report.
 *
 * `enabled` is the caller's switch, because this is a button, not a page load: the query runs an
 * inner join across billing_cycles, resources, tenants and users, and the answer is expected to be
 * an empty list — nobody should pay for it on every visit to the table.
 *
 * `staleTime: 0` on purpose, unlike the list: an operator presses this again precisely because they
 * just repaired a row and want to see it gone.
 */
export function useResourcesWithoutCycle(enabled: boolean, page: number, pageSize: number) {
  return useQuery({
    queryKey: billingCycleKeys.resourcesWithoutCycle(page, pageSize),
    queryFn: () => adminBillingCycleService.getResourcesWithoutCycle(page, pageSize),
    enabled,
    staleTime: 0,
    // Keeps the current page on screen while the next one loads, so paging does not blank the
    // table and jump the page back to the top.
    placeholderData: (previousData) => previousData,
  })
}

export function useExpiryMismatches(enabled: boolean, page: number, pageSize: number) {
  return useQuery({
    queryKey: billingCycleKeys.expiryMismatches(page, pageSize),
    queryFn: () => adminBillingCycleService.getExpiryMismatches(page, pageSize),
    enabled,
    staleTime: 0,
    placeholderData: (previousData) => previousData,
  })
}
